import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS, stanAnalytics, stanNotify } from '@/lib';
import { createFlow } from '@/lib/api/email-flows';
import { useUser } from '@/queries';
import { FlowFormData } from '../validations/flowForm';
import { buildFlowSubmissionData, validateProductSelection } from '../logic/addFlow.logic';
import { FLOW_TRIGGER } from '../constants/flowTriggers';
import { isScheduledTimeValid } from '../logic/timeValidation.logic';

export const useFlowSubmission = () => {
    const { user } = useUser();
    const queryClient = useQueryClient();

    const { mutateAsync: submitFlow, isPending } = useMutation({
        mutationFn: async (formData: FlowFormData) => {
            const isPurchaseTrigger = formData.selectedTrigger === FLOW_TRIGGER.CHECKOUT;
            const isTimeTrigger = formData.selectedTrigger === FLOW_TRIGGER.TIME;

            if (isPurchaseTrigger) {
                const validation = validateProductSelection(
                    formData.selectedProducts,
                    formData.selectedExcludeProducts
                );

                if (!validation.isValid) {
                    throw new Error("You can't have the same product for trigger and removal!");
                }
            }

            if (isTimeTrigger && formData.selectedDate && formData.selectedTime !== undefined) {
                const validation = isScheduledTimeValid(formData.selectedDate, formData.selectedTime);
                if (!validation.isValid) {
                    throw new Error(
                        validation.error || 'Please schedule the flow for at least 10 minutes in the future.'
                    );
                }
            }

            const flowData = buildFlowSubmissionData(formData);
            const response = await createFlow(flowData);
            return response;
        },
        onSuccess: data => {
            const flowId = data.flow_id;

            if (flowId > 0) {
                // Track analytics
                stanAnalytics('flow-created', {
                    meta: { user_id: user?.user_id, username: user?.username },
                    props: { flow_id: flowId },
                });

                // Invalidate flows query to refetch the list
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMAIL_FLOWS_LIST] });

                // Show success notification
                stanNotify({
                    type: 'success',
                    title: 'Flow created successfully',
                });
            }
        },
        onError: (error: unknown) => {
            if (error instanceof Error) {
                if (error.message === "You can't have the same product for trigger and removal!") {
                    stanNotify({
                        type: 'error',
                        title: 'Wait up!',
                        text: error.message,
                    });
                    return;
                }

                if (error.message.includes('schedule') || error.message.includes('10 minutes')) {
                    stanNotify({
                        type: 'error',
                        title: 'Invalid Time',
                        text: error.message,
                    });
                    return;
                }
            }

            // Handle API error with response
            if (
                error &&
                typeof error === 'object' &&
                'response' in error &&
                error.response &&
                typeof error.response === 'object'
            ) {
                const response = error.response as { status?: number; data?: { message?: string } };

                if (response.status === 409) {
                    stanNotify({
                        type: 'error',
                        title: 'Wait up!',
                        text:
                            response.data?.message ||
                            'An identical email flow already exists with the same audience/product/trigger.',
                    });
                    return;
                }
            }

            // Fallback for any other error
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: 'Please try again or contact support',
            });
        },
    });

    return {
        submitFlow,
        isLoading: isPending,
    };
};
